import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";

import { AuthProvider, useAuth } from "./context/AuthContext";
import AgeGate from "./components/AgeGate";
import Layout from "./components/Layout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import Dashboard from "./pages/Dashboard";
import Calculator from "./pages/Calculator";
import Tracker from "./pages/Tracker";
import Profile from "./pages/Profile";
import Strategy from "./pages/Strategy";
import TrackRecord from "./pages/TrackRecord";
import Pricing from "./pages/Pricing";
import Billing from "./pages/Billing";
import Arbitrage from "./pages/Arbitrage";
import Admin from "./pages/Admin";
import TerminalShell from "./components/terminal/TerminalShell";
import BestEdge from "./pages/terminal/BestEdge";
import Traps from "./pages/terminal/Traps";
import NoBet from "./pages/terminal/NoBet";
import Disagreement from "./pages/terminal/Disagreement";
import Ledger from "./pages/terminal/Ledger";
import UfcTerminal from "./pages/terminal/UfcTerminal";
import MartingaleMethod from "./pages/MartingaleMethod";
import ArbitrageBetting from "./pages/ArbitrageBetting";
import Leaderboard from "./pages/Leaderboard";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ResponsibleGambling from "./pages/ResponsibleGambling";
import About from "./pages/About";
import Contact from "./pages/Contact";

const TOAST_OPTIONS = {
  style: {
    background: "#18181b",
    border: "1px solid #27272a",
    color: "#fafafa",
    fontFamily: "JetBrains Mono, monospace",
    fontSize: 12,
  },
};

function Guard({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="mono text-sm text-zinc-500">authenticating…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Public({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="mono text-sm text-zinc-500">loading…</div>
      </div>
    );
  }
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <AgeGate />
            <Toaster theme="dark" position="top-right" toastOptions={TOAST_OPTIONS} />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/ledger" element={
                <div className="min-h-screen bg-background grain relative">
                  <main className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-10">
                    <Ledger />
                  </main>
                </div>
              } />
              <Route path="/martingale-method" element={<MartingaleMethod />} />
              <Route path="/arbitrage-betting" element={<ArbitrageBetting />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/responsible-gambling" element={<ResponsibleGambling />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/login"
                element={
                  <Public>
                    <Login />
                  </Public>
                }
              />
              <Route
                path="/register"
                element={
                  <Public>
                    <Register />
                  </Public>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <Public>
                    <ForgotPassword />
                  </Public>
                }
              />
              <Route
                path="/app"
                element={
                  <Guard>
                    <Layout />
                  </Guard>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="calculator" element={<Calculator />} />
                <Route path="strategy" element={<Strategy />} />
                <Route path="tracker" element={<Tracker />} />
                <Route path="track-record" element={<TrackRecord />} />
                <Route path="arb" element={<Arbitrage />} />
                <Route path="terminal" element={<TerminalShell />}>
                  <Route index element={<BestEdge />} />
                  <Route path="traps" element={<Traps />} />
                  <Route path="no-bet" element={<NoBet />} />
                  <Route path="disagreement" element={<Disagreement />} />
                  <Route path="ledger" element={<Ledger />} />
                  <Route path="ufc" element={<UfcTerminal />} />
                </Route>
                <Route path="billing" element={<Billing />} />
                <Route path="profile" element={<Profile />} />
                <Route path="admin" element={<Admin />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
